/**
 * Sparkline.js — Lightweight SVG sparkline for KPI trends.
 * No external library, ~60 lines, zero deps.
 */
import React, { useMemo } from 'react';

export default function Sparkline({
    points = [],
    width = 240,
    height = 56,
    color = '#FF914D',
    fillOpacity = 0.15,
    showLast = true,
    synthetic = false,
}) {
    const compact = height < 40;
    const data = useMemo(() => points.map(p => Number(p?.value ?? 0)), [points]);

    if (!data.length) {
        return compact ? null : (
            <div
                className="flex items-center justify-center text-[10px] text-muted-foreground/50"
                style={{ height }}
            >
                Pas encore d'historique
            </div>
        );
    }

    const min = Math.min(...data);
    const max = Math.max(...data);
    const range = max - min || 1;
    const stepX = data.length > 1 ? width / (data.length - 1) : width;

    const coords = data.map((v, i) => {
        const x = i * stepX;
        const y = height - ((v - min) / range) * (height - 8) - 4;
        return [x, y];
    });

    const linePath = coords
        .map(([x, y], i) => (i === 0 ? `M${x.toFixed(1)},${y.toFixed(1)}` : `L${x.toFixed(1)},${y.toFixed(1)}`))
        .join(' ');

    const areaPath = `${linePath} L${(width).toFixed(1)},${height} L0,${height} Z`;

    const last = coords[coords.length - 1];
    const first = coords[0];
    const trend = data[data.length - 1] - data[0];
    const trendColor = trend > 0 ? '#10b981' : trend < 0 ? '#f43f5e' : '#94a3b8';

    return (
        <div className="relative" data-testid="sparkline">
            <svg
                width="100%"
                height={height}
                viewBox={`0 0 ${width} ${height}`}
                preserveAspectRatio="none"
                style={{ display: 'block', overflow: 'visible' }}
            >
                <defs>
                    <linearGradient id="spark-fill" x1="0" x2="0" y1="0" y2="1">
                        <stop offset="0%" stopColor={color} stopOpacity={fillOpacity} />
                        <stop offset="100%" stopColor={color} stopOpacity={0} />
                    </linearGradient>
                </defs>
                <path d={areaPath} fill="url(#spark-fill)" />
                <path
                    d={linePath}
                    fill="none"
                    stroke={color}
                    strokeWidth="1.5"
                    strokeLinejoin="round"
                    strokeLinecap="round"
                    strokeDasharray={synthetic ? '3 3' : '0'}
                    opacity={synthetic ? 0.7 : 1}
                />
                {showLast && last && (
                    <>
                        <circle cx={last[0]} cy={last[1]} r="3" fill={color} stroke="#0b0b0f" strokeWidth="1.5" />
                    </>
                )}
                {first && (
                    <circle cx={first[0]} cy={first[1]} r="2" fill={color} opacity="0.5" />
                )}
            </svg>
            {!compact && (
                <div className="absolute bottom-0 right-0 flex items-center gap-1 text-[9px] text-muted-foreground">
                    <span style={{ color: trendColor }} className="font-bold">
                        {trend > 0 ? '↑' : trend < 0 ? '↓' : '→'}
                    </span>
                    <span>14 jours</span>
                    {synthetic && <span className="opacity-60">· estimé</span>}
                </div>
            )}
        </div>
    );
}
